import { API_ENDPOINTS } from '@/config/apiEndpoints';
import type { DocumentId, ProjectId, RevisionId } from '@/shared/types/common.types';
import { apiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import { toCursorPage } from '@/features/workspace/api/workspaceContracts';
import type { CursorPage } from '@/features/workspace/types/workspace.types';
import {
  contentUrlSchema,
  documentDetailSchema,
  documentFacetsSchema,
  documentPageSchema,
  revisionPageSchema,
  revisionSchema,
  sheetPageSchema,
  toContentUrl,
  toDocumentDetail,
  toDocumentFacets,
  toDocumentRevision,
  toDocumentSummary,
  toSheetRow,
} from '../api/documentContracts';
import type {
  ContentUrl,
  CreateDocumentInput,
  DocumentDetail,
  DocumentFacets,
  DocumentListFilters,
  DocumentRevision,
  DocumentSummary,
  NewRevisionInput,
  RevisionStatus,
  SheetListFilters,
  SheetRow,
  UpdateDocumentInput,
} from '../types/document.types';

/** v1 routes bind repeated keys (`status=Shared&status=Published`), not Axios's `status[]=`. */
const REPEATED_KEYS = { indexes: null } as const;

function filterParams(filters: Partial<DocumentListFilters>): Record<string, unknown> {
  return {
    type: filters.type ?? undefined,
    status: filters.statuses !== undefined && filters.statuses.length > 0 ? filters.statuses : undefined,
    discipline:
      filters.disciplines !== undefined && filters.disciplines.length > 0 ? filters.disciplines : undefined,
    q: filters.q !== undefined && filters.q.length > 0 ? filters.q : undefined,
    deleted: filters.deleted === true ? true : undefined,
  };
}

export const documentService = {
  async list(
    projectId: ProjectId,
    filters: DocumentListFilters,
    page: { readonly limit: number; readonly cursor?: string }
  ): Promise<CursorPage<DocumentSummary>> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.documents.list(projectId), {
      params: {
        ...filterParams(filters),
        sort: filters.sort,
        order: filters.sort === 'updatedAt' ? 'desc' : 'asc',
        limit: page.limit,
        cursor: page.cursor,
      },
      paramsSerializer: REPEATED_KEYS,
    });
    return toCursorPage(parseOrThrow(documentPageSchema, payload, 'document list'), toDocumentSummary);
  },

  /** The sheet register: Sheet documents by number. */
  async sheets(
    projectId: ProjectId,
    filters: SheetListFilters,
    page: { readonly limit: number; readonly cursor?: string }
  ): Promise<CursorPage<SheetRow>> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.documents.sheets(projectId), {
      params: {
        discipline: filters.disciplines.length > 0 ? filters.disciplines : undefined,
        q: filters.q.length > 0 ? filters.q : undefined,
        limit: page.limit,
        cursor: page.cursor,
      },
      paramsSerializer: REPEATED_KEYS,
    });
    return toCursorPage(parseOrThrow(sheetPageSchema, payload, 'sheet list'), toSheetRow);
  },

  /** Counts for the filter chips, under the same filters as the list. */
  async facets(projectId: ProjectId, filters: Partial<DocumentListFilters>): Promise<DocumentFacets> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.documents.facets(projectId), {
      params: filterParams(filters),
      paramsSerializer: REPEATED_KEYS,
    });
    return toDocumentFacets(parseOrThrow(documentFacetsSchema, payload, 'document facets'));
  },

  /**
   * Creates the document from a completed upload. `idempotencyKey` is generated once per
   * attempt, so a retried request Core already handled returns the same document.
   */
  async create(
    projectId: ProjectId,
    input: CreateDocumentInput,
    idempotencyKey: string
  ): Promise<DocumentDetail> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.documents.list(projectId), input, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return toDocumentDetail(parseOrThrow(documentDetailSchema, payload, 'created document'));
  },

  async get(projectId: ProjectId, documentId: DocumentId): Promise<DocumentDetail> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.documents.document(projectId, documentId));
    return toDocumentDetail(parseOrThrow(documentDetailSchema, payload, 'document'));
  },

  /** Newest first. */
  async revisions(
    projectId: ProjectId,
    documentId: DocumentId,
    cursor?: string
  ): Promise<CursorPage<DocumentRevision>> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.documents.revisions(projectId, documentId), {
      params: { cursor },
    });
    return toCursorPage(parseOrThrow(revisionPageSchema, payload, 'revision list'), toDocumentRevision);
  },

  /** `etag` is the one the change was based on; Core answers `412` if someone saved since. */
  async update(
    projectId: ProjectId,
    documentId: DocumentId,
    input: UpdateDocumentInput,
    etag: string
  ): Promise<DocumentDetail> {
    const payload = await apiClient.patch<unknown>(
      API_ENDPOINTS.documents.document(projectId, documentId),
      input,
      {
        headers: { 'If-Match': etag },
      }
    );
    return toDocumentDetail(parseOrThrow(documentDetailSchema, payload, 'document'));
  },

  async remove(projectId: ProjectId, documentId: DocumentId, etag: string): Promise<void> {
    await apiClient.delete<unknown>(API_ENDPOINTS.documents.document(projectId, documentId), {
      headers: { 'If-Match': etag },
    });
  },

  async restore(projectId: ProjectId, documentId: DocumentId): Promise<DocumentDetail> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.documents.restore(projectId, documentId));
    return toDocumentDetail(parseOrThrow(documentDetailSchema, payload, 'document'));
  },

  async addRevision(
    projectId: ProjectId,
    documentId: DocumentId,
    input: NewRevisionInput,
    etag: string,
    idempotencyKey: string
  ): Promise<DocumentRevision> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.documents.revisions(projectId, documentId),
      input,
      {
        headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey },
      }
    );
    return toDocumentRevision(parseOrThrow(revisionSchema, payload, 'created revision'));
  },

  async changeStatus(
    projectId: ProjectId,
    documentId: DocumentId,
    revisionId: RevisionId,
    status: Exclude<RevisionStatus, 'Unknown'>
  ): Promise<DocumentRevision> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.documents.revisionStatus(projectId, documentId, revisionId),
      { status }
    );
    return toDocumentRevision(parseOrThrow(revisionSchema, payload, 'revision'));
  },

  /** A short-lived storage URL: `attachment` downloads, `inline` previews. */
  async contentUrl(
    projectId: ProjectId,
    documentId: DocumentId,
    revisionId: RevisionId,
    disposition: 'inline' | 'attachment'
  ): Promise<ContentUrl> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.documents.contentUrl(projectId, documentId, revisionId),
      { disposition }
    );
    return toContentUrl(parseOrThrow(contentUrlSchema, payload, 'content URL'));
  },
};
